import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, UserCog, Pencil, Ban, CheckCircle, Shield } from 'lucide-react';

export default function UsersPage() {
  const { user: currentUser, canManageRole, getRoleLevel } = useAuth();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const isMaster = currentUser?.role === 'master';
  const isAdminGeral = currentUser?.role === 'admin_geral' || isMaster;
  const isAdminOnly = currentUser?.role === 'admin';
  const currentLevel = getRoleLevel(currentUser?.role);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'marcador',
    unit_id: isAdminOnly ? (currentUser?.unit_id || '') : ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, unitsRes] = await Promise.all([
        api.get('/users'),
        api.get('/health-units')
      ]);
      setUsers(usersRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Edição
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          unit_id: formData.unit_id
        };
        
        // Só incluir senha se foi preenchida
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        // Criação
        await api.post('/auth/register', formData);
        toast.success('Usuário criado com sucesso!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Não preencher senha na edição
      role: user.role,
      unit_id: user.unit_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleToggleBlock = async (userId, userName, isCurrentlyBlocked) => {
    const acao = isCurrentlyBlocked ? 'desbloquear' : 'bloquear';
    if (!window.confirm(`Tem certeza que deseja ${acao} o usuário "${userName}"?`)) return;
    
    try {
      const response = await api.put(`/users/${userId}/block`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao ${acao} usuário`);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'marcador',
      unit_id: ''
    });
    setEditingUser(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Usuários</h1>
          <p className="text-slate-600">Gerencie os usuários do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-10 px-6 font-medium shadow-sm"
              onClick={() => resetForm()}
              data-testid="add-user-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  placeholder="NOME DO USUÁRIO"
                  required
                  className="h-11 rounded-lg uppercase"
                  data-testid="user-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  required
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="password">
                  Senha {editingUser ? '(deixe em branco para manter)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Nova senha (opcional)' : 'Senha forte'}
                  required={!editingUser}
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="role">Nivel de Acesso *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, unit_id: ['admin', 'marcador'].includes(value) ? formData.unit_id : '' })}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="role-select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isMaster && <SelectItem value="admin_geral">Administrador Geral</SelectItem>}
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="marcador_geral">Marcador Geral</SelectItem>
                    <SelectItem value="marcador">Marcador</SelectItem>
                    <SelectItem value="relator_hospital">Relator - Hospital</SelectItem>
                    <SelectItem value="relator_lab_municipal">Relator - Lab. Municipal</SelectItem>
                  </SelectContent>
                </Select>
                {['relator_hospital', 'relator_lab_municipal'].includes(formData.role) && (
                  <p className="text-xs text-slate-500 mt-1">
                    O Relator so podera emitir relatorios da {formData.role === 'relator_hospital' ? 'unidade Hospital' : 'unidade Lab. Municipal'} a qualquer momento.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="unit">Unidade de Saude {['admin', 'marcador'].includes(formData.role) ? '*' : '(opcional)'}</Label>
                <Select 
                  value={formData.unit_id} 
                  onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                  disabled={isAdminOnly}
                >
                  <SelectTrigger className="h-11 rounded-lg">
                    <SelectValue placeholder="Selecione uma unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isAdminOnly && (
                  <p className="text-xs text-slate-500 mt-1">Administrador só pode criar usuários para sua própria unidade</p>
                )}
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="save-user-button"
                >
                  {editingUser ? 'Atualizar' : 'Criar'} Usuário
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold text-xs h-12">
              <tr>
                <th className="px-6 py-3">Usuário</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Nível</th>
                <th className="px-6 py-3">Unidade</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user) => {
                  const isMasterUser = user.role === 'master';
                  const isBlocked = user.is_blocked;
                  
                  return (
                    <tr key={user.id} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors h-14 ${isBlocked ? 'bg-red-50/30' : ''}`}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            isMasterUser ? 'bg-amber-100' : isBlocked ? 'bg-red-100' : 'bg-violet-50'
                          }`}>
                            {isMasterUser ? (
                              <Shield className="h-5 w-5 text-amber-600" />
                            ) : isBlocked ? (
                              <Ban className="h-5 w-5 text-red-500" />
                            ) : (
                              <UserCog className="h-5 w-5 text-violet-600" />
                            )}
                          </div>
                          <div>
                            <span className={`font-medium ${isBlocked ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                              {user.name}
                            </span>
                            {isMasterUser && (
                              <span className="block text-xs text-amber-600 font-medium">MASTER</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-3 ${isBlocked ? 'text-slate-400' : 'text-slate-600'}`}>{user.email}</td>
                      <td className="px-6 py-3">
                        <span
                          className={
                            `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              user.role === 'master'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : user.role === 'admin_geral'
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                : user.role === 'admin'
                                ? 'bg-teal-50 text-teal-700 border border-teal-200'
                                : user.role === 'marcador_geral'
                                ? 'bg-violet-50 text-violet-700 border border-violet-200'
                                : 'bg-sky-50 text-sky-700 border border-sky-200'
                            }`
                          }
                        >
                          {{ master: 'Master', admin_geral: 'Adm. Geral', admin: 'Administrador', marcador_geral: 'Marcador Geral', marcador: 'Marcador', relator_hospital: 'Relator Hospital', relator_lab_municipal: 'Relator Lab. Mun.' }[user.role] || user.role}
                        </span>
                      </td>
                      <td className={`px-6 py-3 ${isBlocked ? 'text-slate-400' : 'text-slate-600'}`}>
                        {user.unit_id ? (
                          units.find(unit => unit.id === user.unit_id)?.name || 'Unidade não encontrada'
                        ) : (
                          <span className="text-slate-400 italic">Não atribuída</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        {isMasterUser ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                            <Shield className="h-3 w-3" />
                            Protegido
                          </span>
                        ) : isBlocked ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                            <Ban className="h-3 w-3" />
                            Bloqueado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            <CheckCircle className="h-3 w-3" />
                            Ativo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          {/* Editar - só se pode gerenciar esse role */}
                          {(canManageRole(user.role) || user.id === currentUser?.id) && user.role !== 'master' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(user)}
                              className="h-8"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {/* Bloquear/Desbloquear - só quem tem nível superior */}
                          {canManageRole(user.role) && user.role !== 'master' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleBlock(user.id, user.name, isBlocked)}
                              className={`h-8 ${isBlocked 
                                ? 'text-green-600 hover:bg-green-50 hover:border-green-200' 
                                : 'text-amber-600 hover:bg-amber-50 hover:border-amber-200'
                              }`}
                              title={isBlocked ? 'Desbloquear usuario' : 'Bloquear usuario'}
                              data-testid={`block-user-${user.id}`}
                            >
                              {isBlocked ? <CheckCircle className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                          {/* Excluir - só quem tem nível superior, nunca master */}
                          {canManageRole(user.role) && user.role !== 'master' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(user.id)}
                              className="h-8 text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                              title="Excluir usuario"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    Nenhum usuário cadastrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}